import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatINR } from "@/lib/sections";
import { toast, Toaster } from "sonner";
import { Plus, Pencil, Trash2, X, Upload } from "lucide-react";

const EMPTY = {
  title: "",
  artist: "",
  medium: "",
  size: "",
  year: "",
  price_inr: 0,
  image_url: "",
  description: "",
  is_available: true,
};

export default function AdminArtworks() {
  const [items, setItems] = useState([]);
  const [artists, setArtists] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [aw, ar] = await Promise.all([
        api.get("/admin/artworks"),
        api.get("/artists"),
      ]);
      setItems(aw.data);
      setArtists(ar.data);
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY); setEditingId(null); setOpen(true); };
  const openEdit = (a) => { setForm({ ...EMPTY, ...a }); setEditingId(a.id); setOpen(true); };

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "artwork-for-sale");
    setUploading(true);
    try {
      const { data } = await api.post("/gallery/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Upload failed"));
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, price_inr: Number(form.price_inr) || 0 };
      if (!payload.image_url) return toast.error("Please upload an artwork image");
      if (!payload.artist) return toast.error("Please select an artist");
      if (editingId) {
        await api.put(`/admin/artworks/${editingId}`, payload);
        toast.success("Artwork updated");
      } else {
        await api.post("/admin/artworks", payload);
        toast.success("Artwork added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const del = async (a) => {
    if (!window.confirm(`Delete "${a.title}" by ${a.artist}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/artworks/${a.id}`);
      toast.success("Artwork deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleAvailable = async (a) => {
    try {
      await api.put(`/admin/artworks/${a.id}`, { is_available: !a.is_available });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-artworks-page">
      <Toaster position="top-center" richColors />
      <div className="flex items-baseline justify-between mb-8">
        <div>
          <div className="overline">Inventory</div>
          <h1 className="display text-4xl mt-2">Artworks</h1>
          <p className="text-muted2 text-sm mt-2">{items.length} piece{items.length !== 1 ? "s" : ""} across {new Set(items.map(x => x.artist)).size} artist{new Set(items.map(x => x.artist)).size !== 1 ? "s" : ""}</p>
        </div>
        <button onClick={openCreate} className="btn-primary" data-testid="artwork-add-btn">
          <Plus size={14} /> Add artwork
        </button>
      </div>

      {loading ? (
        <div className="text-muted2">Loading…</div>
      ) : (
        <div className="grid gap-3">
          {items.map(a => (
            <div key={a.id} className="border border-soft bg-white p-4 flex gap-4 items-center" data-testid={`artwork-row-${a.id.slice(0,8)}`}>
              <div className="w-20 h-24 bg-cream-2 border border-soft overflow-hidden flex items-center justify-center p-1 shrink-0">
                <img src={a.image_url} alt={a.title} className="max-w-full max-h-full w-auto h-auto object-contain" />
              </div>
              <div className="flex-1">
                <div className="font-serif text-lg italic">{a.title}</div>
                <div className="text-xs text-muted2 mt-0.5">{a.artist} · {a.medium} · {a.size}{a.year ? ` · ${a.year}` : ""}</div>
                <div className="text-sm mt-1">{formatINR(a.price_inr)}</div>
              </div>
              <button
                onClick={() => toggleAvailable(a)}
                className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${a.is_available ? "border-sage text-sage" : "border-soft text-muted2"}`}
                data-testid={`artwork-toggle-${a.id.slice(0,8)}`}
                title={a.is_available ? "Mark as sold" : "Mark available"}
              >
                {a.is_available ? "Available" : "Sold"}
              </button>
              <button onClick={() => openEdit(a)} className="p-2 hover:bg-cream-2" data-testid={`artwork-edit-${a.id.slice(0,8)}`}><Pencil size={14} /></button>
              <button onClick={() => del(a)} className="p-2 hover:bg-cream-2 text-red-700" data-testid={`artwork-delete-${a.id.slice(0,8)}`}><Trash2 size={14} /></button>
            </div>
          ))}
          {items.length === 0 && <div className="text-muted2">No artworks yet. Add your first piece.</div>}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 md:p-10 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="bg-cream max-w-2xl w-full border border-soft" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-soft">
              <div className="font-serif text-2xl">{editingId ? "Edit artwork" : "Add artwork"}</div>
              <button onClick={() => setOpen(false)} data-testid="artwork-modal-close"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4" data-testid="artwork-form">
              {/* Image upload */}
              <div>
                <div className="overline mb-2">Artwork image *</div>
                {form.image_url ? (
                  <div className="relative w-40 h-48 border border-soft bg-cream-2 flex items-center justify-center p-1">
                    <img src={form.image_url} alt="" className="max-w-full max-h-full w-auto h-auto object-contain" />
                    <button type="button" onClick={() => setForm(f => ({ ...f, image_url: "" }))} className="absolute -top-2 -right-2 bg-charcoal text-cream p-1 rounded-full"><X size={12} /></button>
                  </div>
                ) : (
                  <label className="inline-flex items-center gap-2 border border-dashed border-soft px-4 py-3 cursor-pointer hover:bg-cream-2 text-sm" data-testid="artwork-upload-label">
                    <Upload size={14} />
                    {uploading ? "Uploading…" : "Choose file (JPG / PNG / WEBP)"}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])}
                      data-testid="artwork-image-input"
                    />
                  </label>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="overline">Title *</span>
                  <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-transparent border-b border-soft py-2 text-sm focus:outline-none focus:border-charcoal" data-testid="artwork-title" />
                </label>
                <label className="block">
                  <span className="overline">Artist *</span>
                  <input
                    required
                    list="artist-suggestions"
                    value={form.artist}
                    onChange={(e) => setForm({ ...form, artist: e.target.value })}
                    className="w-full bg-transparent border-b border-soft py-2 text-sm focus:outline-none focus:border-charcoal"
                    data-testid="artwork-artist"
                    placeholder="Type or pick"
                  />
                  <datalist id="artist-suggestions">
                    {artists.map(a => <option key={a.id} value={a.name} />)}
                  </datalist>
                </label>
                <label className="block">
                  <span className="overline">Medium</span>
                  <input value={form.medium} onChange={(e) => setForm({ ...form, medium: e.target.value })} className="w-full bg-transparent border-b border-soft py-2 text-sm focus:outline-none focus:border-charcoal" placeholder="Oil on canvas" data-testid="artwork-medium" />
                </label>
                <label className="block">
                  <span className="overline">Size</span>
                  <input value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} className="w-full bg-transparent border-b border-soft py-2 text-sm focus:outline-none focus:border-charcoal" placeholder="48 × 36 in" data-testid="artwork-size" />
                </label>
                <label className="block">
                  <span className="overline">Year</span>
                  <input value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="w-full bg-transparent border-b border-soft py-2 text-sm focus:outline-none focus:border-charcoal" placeholder="2025" data-testid="artwork-year" />
                </label>
                <label className="block">
                  <span className="overline">Price (INR) *</span>
                  <input required type="number" value={form.price_inr} onChange={(e) => setForm({ ...form, price_inr: e.target.value })} className="w-full bg-transparent border-b border-soft py-2 text-sm focus:outline-none focus:border-charcoal" data-testid="artwork-price" />
                </label>
              </div>

              <label className="block">
                <span className="overline">Description / provenance</span>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full bg-transparent border-b border-soft py-2 text-sm focus:outline-none focus:border-charcoal" data-testid="artwork-description" />
              </label>

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_available} onChange={(e) => setForm({ ...form, is_available: e.target.checked })} data-testid="artwork-available" />
                Available for sale
              </label>

              <div className="flex justify-end gap-3 pt-4 border-t border-soft">
                <button type="button" onClick={() => setOpen(false)} className="btn-ghost" data-testid="artwork-cancel">Cancel</button>
                <button type="submit" className="btn-primary" disabled={uploading} data-testid="artwork-submit">
                  {editingId ? "Save changes" : "Add artwork"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
